import { useMutation } from '@apollo/client';
import type {
  InsertAssessmentActivityWithLinkedItemsMutation,
  InsertAssessmentActivityWithLinkedItemsMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertAssessmentActivityWithLinkedItemsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useInsertAssessmentActivityWithLinkedItemsTRPC } from './useInsertAssessmentActivityWithLinkedItemsTRPC';

export const useInsertAssessmentActivityWithLinkedItems = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation (legacy)
  const [insertAssessmentActivityGraphQL, graphqlState] = useMutation(
    InsertAssessmentActivityWithLinkedItemsDocument,
    {
      update: (cache) => {
        evictField(cache, 'assessment');
        evictField(cache, 'compliance_monitoring_assessment');
        evictField(cache, 'internal_audit_entity');
        evictField(cache, 'internal_audit_report');
        evictField(cache, 'assessment_activity');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useInsertAssessmentActivityWithLinkedItemsTRPC();

  const insertAssessmentActivity = async (
    variables: InsertAssessmentActivityWithLinkedItemsMutationVariables
  ): Promise<InsertAssessmentActivityWithLinkedItemsMutation> => {
    if (trpcEnabled) {
      return trpcMutation.insertAssessmentActivity({
        ActivityType: variables.ActivityType,
        ParentId: variables.ParentId,
        Status: variables.Status,
        Summary: variables.Summary,
        Title: variables.Title,
        CompletionDate: variables.CompletionDate,
        AssignedUser: variables.AssignedUser,
        LinkedItemIds:
          variables.LinkedItemIds == null
            ? []
            : Array.isArray(variables.LinkedItemIds)
              ? variables.LinkedItemIds
              : [variables.LinkedItemIds],
        CustomAttributeData: variables.CustomAttributeData,
        IsRCSA: variables.IsRCSA ?? undefined,
        RiskId: variables.RiskId,
        OwnerUserIds:
          variables.OwnerUserIds == null
            ? []
            : Array.isArray(variables.OwnerUserIds)
              ? variables.OwnerUserIds
              : [variables.OwnerUserIds],
        OwnerGroupIds:
          variables.OwnerGroupIds == null
            ? []
            : Array.isArray(variables.OwnerGroupIds)
              ? variables.OwnerGroupIds
              : [variables.OwnerGroupIds],
      });
    }

    const result = await insertAssessmentActivityGraphQL({
      variables,
    });

    if (!result.data) {
      throw new Error('Failed to insert assessment activity');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      insertAssessmentActivity,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertAssessmentActivity,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
